import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, CheckCircle } from 'lucide-react';
import cleanbidLogo from '../assets/cleanbid-logo.png';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [validCode, setValidCode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const navigate = useNavigate();

  const oobCode = searchParams.get('oobCode');

  // Verify the reset code on mount
  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setError('Invalid or missing reset code.');
        setIsVerifying(false);
        return;
      }

      try {
        const email = await verifyPasswordResetCode(auth, oobCode);
        setResetEmail(email);
        setValidCode(true);
      } catch (err) {
        console.error('Code verification error:', err);
        
        if (err.code === 'auth/invalid-action-code') {
          setError('This password reset link has expired or is invalid.');
        } else {
          setError('Failed to verify reset code. Please request a new reset link.');
        }
      } finally {
        setIsVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode]);

  const validateForm = () => {
    if (!newPassword) {
      setError('Password is required');
      return false;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);

      // After password reset, sign in (if we know the email) and mark password as set
      try {
        let idToken = null;

        if (resetEmail) {
          try {
            const cred = await signInWithEmailAndPassword(auth, resetEmail, newPassword);
            idToken = await cred.user.getIdToken();
          } catch (signInErr) {
            console.error('Post-reset sign-in failed (may be optional):', signInErr);
          }
        }

        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        if (idToken) {
          try {
            const resp = await fetch(`${backendUrl}/api/user/mark-password-set`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${idToken}`,
              },
            });
            const data = await resp.json().catch(() => ({}));
            console.log('[ResetPassword] mark-password-set response', resp.status, data);
          } catch (markErr) {
            console.error('Failed to mark password set:', markErr);
          }
        } else {
          console.warn('No ID token available to mark password set; user may need to log in manually.');
        }
      } catch (flowErr) {
        console.error('Post-reset flow error:', flowErr);
      }

      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      
      if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else {
        setError(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
        <div className="text-center space-y-4">
          <div className="h-12 w-12 border-4 border-[#71DED6] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-lg font-medium text-muted-foreground">Verifying reset code...</p>
        </div>
      </div>
    );
  }

  if (!validCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 p-4">
        <Card className="w-full max-w-md p-8 shadow-xl">
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          
          <div className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Please request a new password reset link.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full hover:opacity-90" style={{ backgroundColor: '#71DED6', color: '#10151D' }}>
                Request New Link
              </Button>
            </Link>
            <Link
              to="/login"
              className="inline-block text-sm hover:underline font-semibold"
              style={{ color: '#116F73' }}
            >
              Back to Login
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 p-4">
      <Card className="w-full max-w-md p-8 shadow-xl">
        {/* CleanBid.ai logo */}
        <img
          src={cleanbidLogo}
          alt="CleanBid.ai"
          className="w-32 mx-auto mb-6 object-contain"
        />

        <div className="flex flex-col items-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--cb-navy)' }}>
            Reset Password
          </h1>
          <p className="text-muted-foreground text-center">
            Enter your new password below
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success ? (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <CheckCircle className="h-16 w-16 text-green-600" />
            </div>
            <Alert className="bg-green-50 border-green-200">
              <AlertDescription className="text-green-800 text-center">
                Password reset successfully! Redirecting to login...
              </AlertDescription>
            </Alert>
            <Link to="/login">
              <Button className="w-full hover:opacity-90" style={{ backgroundColor: '#71DED6', color: '#10151D' }}>
                Go to Login
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="At least 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full shadow-sm"
              style={{ backgroundColor: '#71DED6', color: '#10151D' }}
              size="lg"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-[#10151D] border-t-transparent rounded-full animate-spin" />
                  Resetting password...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Reset Password
                </span>
              )}
            </Button>
          </form>
        )}

        <div className="mt-8 text-center text-sm">
          <p className="text-muted-foreground">
            Powered by <span className="font-semibold" style={{ color: 'var(--cb-navy)' }}>CleanBid.ai</span>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ResetPassword;